/**
 * Tab Stack Renderers - render the correct screen based on current tab stack
 * Each tab has its own renderer function that maps screen names to components
 */

import type { TabStackEntry } from '../types/domain';

// Home tab screens
import { HomeScreen } from '../screens/Home/HomeScreen';
import { AssistantDetailScreen } from '../screens/Home/AssistantDetailScreen';
import { EventListScreen } from '../screens/Events/EventListScreen';
import { EventDetailScreen } from '../screens/Events/EventDetailScreen';

// Athletes tab screens
import { AthletesScreen } from '../screens/Athletes/AthletesScreen';
import { AthleteDetailScreen } from '../screens/Athletes/AthleteDetailScreen';
import { ImportAthletesScreen } from '../screens/Athletes/ImportAthletesScreen';
import { AthletePaymentsScreen } from '../screens/Athletes/AthletePaymentsScreen';

// Groups tab screens
import { GroupsScreen } from '../screens/Groups/GroupsScreen';
import { GroupDetailScreen } from '../screens/Groups/GroupDetailScreen';
import { GroupPaymentsScreen } from '../screens/Groups/GroupPaymentsScreen';
import { PaymentSettingsScreen } from '../screens/Groups/PaymentSettingsScreen';
import { PaymentOverridesScreen } from '../screens/Groups/PaymentOverridesScreen';
import { CoachListScreen } from '../screens/Coaches/CoachListScreen';
import { CoachDetailScreen } from '../screens/Coaches/CoachDetailScreen';

// Profile tab screens
import { ProfileScreen } from '../screens/Profile/ProfileScreen';
import { FeedbackScreen } from '../screens/Profile/FeedbackScreen';
import { SubscriptionManageScreen } from '../screens/Subscription/SubscriptionManageScreen';
import { SubscriptionPlansScreen } from '../screens/Subscription/SubscriptionPlansScreen';
import { TierChangeConfirmScreen } from '../screens/Subscription/TierChangeConfirmScreen';
import { SubscriptionHelpScreen } from '../screens/Subscription/SubscriptionHelpScreen';
import { OrganizationDetailScreen } from '../screens/Organization/OrganizationDetailScreen';
import { OrganizationSettingsScreen } from '../screens/Organization/OrganizationSettingsScreen';
import { CreateOrganizationScreen } from '../screens/Organization/CreateOrganizationScreen';
import { JoinOrganizationScreen } from '../screens/Organization/JoinOrganizationScreen';

export function renderHomeStack(entry: TabStackEntry) {
  switch (entry.screen) {
    case 'home':
      return <HomeScreen />;
    case 'assistant-detail':
      return <AssistantDetailScreen />;
    case 'events':
      return <EventListScreen />;
    case 'event-detail':
      return <EventDetailScreen />;
    default:
      return <HomeScreen />;
  }
}

export function renderAthletesStack(entry: TabStackEntry) {
  switch (entry.screen) {
    case 'athlete-list':
      return <AthletesScreen />;
    case 'athlete-detail':
      return <AthleteDetailScreen />;
    case 'import-athletes':
      return <ImportAthletesScreen />;
    case 'athlete-payments':
      return <AthletePaymentsScreen />;
    case 'subscription-manage':
      return <SubscriptionManageScreen />;
    case 'subscription-plans':
      return <SubscriptionPlansScreen />;
    default:
      return <AthletesScreen />;
  }
}

export function renderGroupsStack(entry: TabStackEntry) {
  switch (entry.screen) {
    case 'group-list':
      return <GroupsScreen />;
    case 'group-detail':
      return <GroupDetailScreen />;
    case 'athlete-detail':
      return <AthleteDetailScreen />;
    case 'athlete-payments':
      return <AthletePaymentsScreen />;
    case 'group-payments':
      return <GroupPaymentsScreen />;
    case 'payment-settings':
      return <PaymentSettingsScreen />;
    case 'payment-overrides':
      return <PaymentOverridesScreen />;
    case 'coaches':
      return <CoachListScreen />;
    case 'coach-detail':
      return <CoachDetailScreen />;
    case 'subscription-manage':
      return <SubscriptionManageScreen />;
    case 'subscription-plans':
      return <SubscriptionPlansScreen />;
    default:
      return <GroupsScreen />;
  }
}

export function renderProfileStack(entry: TabStackEntry) {
  switch (entry.screen) {
    case 'profile':
      return <ProfileScreen />;
    case 'directions':
      // Directions screen navigates to assistant-detail which is in home tab
      // But it's accessible from profile too via direction switcher
      return <ProfileScreen />;
    case 'feedback':
      return <FeedbackScreen />;
    case 'subscription-manage':
      return <SubscriptionManageScreen />;
    case 'subscription-plans':
      return <SubscriptionPlansScreen />;
    case 'tier-change-confirm':
      return <TierChangeConfirmScreen newTierId={entry.params.newTierId as string} />;
    case 'subscription-help':
      return <SubscriptionHelpScreen />;
    case 'organization-detail':
      return <OrganizationDetailScreen />;
    case 'organization-settings':
      return <OrganizationSettingsScreen />;
    case 'create-organization':
      return <CreateOrganizationScreen />;
    case 'join-organization':
      return <JoinOrganizationScreen />;
    default:
      return <ProfileScreen />;
  }
}
